<script setup lang="ts">
import { computed, ref } from "vue";
import { storeToRefs } from "pinia";

import VimeoEmbed from "@/components/VimeoEmbed.vue";
import FreeNewsletter from "@/components/signups/FreeNewsletterSignup.vue";

import { useApplicationStore } from "@/stores/Application";
import { useAuthStore } from "@/stores/Auth";
import { useContentActions } from "@/composables/content-actions";
import { usePermissionsStore } from "@/stores/Permissions";

import routeManager from "@/router/route-manager";

import type {
  Author,
  ContentfulItem,
  HasAdditionalText,
  HasAuthor, HasEditorialAds,
  HasTextPreview,
  Product
} from "@/types/entities";

import { articleKey, webinarKey } from "@/types/products/product-definitions";
import { isFreelyAvailableContent } from "@/utils/access-utils";
import { countTime, formatDateAsMonthDayYearString } from "@/helpers/dateTimeUtilities";

const { isAuthenticated } = storeToRefs( useAuthStore() );
const permissionsStore = usePermissionsStore();
const { contentItem, contentKey } = storeToRefs( useApplicationStore() );

const authoredContent = ref< HasAuthor | undefined >( contentItem.value as HasAuthor );
const hasEditorialAds = ref< HasEditorialAds | undefined >( contentItem.value as HasEditorialAds );
const majorityTextContent = ref< HasAdditionalText | undefined >( contentItem.value as HasAdditionalText );
const textPreviewContent = ref< HasTextPreview | undefined >( contentItem.value as HasTextPreview );
const content = ref< ContentfulItem >( contentItem.value! );
const contentType = computed< string >( () => contentKey.value! );
const isAuthenticatedArticleOrWebinar = computed< boolean >( () => isAuthenticated.value &&
    ( contentType.value.includes( articleKey ) || contentType.value.includes( webinarKey ) ) );

const author = ref< Author | null >( authoredContent.value?.author ?? null );

const primaryProduct = computed< Product | undefined >( () => content.value?.products.slice( 0, 1 )[ 0 ] ?? { slug: '', name: '---' } );
const primaryProductName = computed< string >( () => primaryProduct.value?.name ?? '---' );

const isPaidProduct = computed< boolean >( () => !isFreelyAvailableContent( content.value?.products.map( p => p.slug ) ) && !!primaryProduct.value );

const contentDateTimeMetaInfo = computed< string >( () => {
  return `${ formatDateAsMonthDayYearString( content.value.publishedDate ) } | ${ countTime( textPreviewContent.value?.text ?? '' ) }`;
} );

const body1 = computed< string >( () => textPreviewContent.value?.text ?? '' );
const body2 = computed< string >( () => majorityTextContent.value?.body2 ? `${ majorityTextContent.value?.body2 }` : '' );
const linkToVideo = computed< string >( () => !!( majorityTextContent.value?.videoLink ) ? `${ majorityTextContent.value.videoLink! }` : '' );

const productRoute = computed( () => primaryProduct.value?.slug ? routeManager.productRouteBySlug( primaryProduct.value.slug ) : "#" );
const authorRoute = computed( () => !!author.value ? routeManager.authorRouteBySlug( author.value.slug ) : "#" );
function tagRoute( tagSlug: string ) {
  return routeManager.tagRouteBySlug( tagSlug );
}

const editorialAd = computed< string | undefined >( () => hasEditorialAds.value?.editorialAd ?? '' );
const adProduct = computed< Product | undefined >( () => hasEditorialAds.value?.adProduct );

const hasAccessToAdvertisedProduct = computed( () => {
  let hasAccess: boolean = false;
  if ( editorialAd.value && adProduct.value ) {
    const adShortCodes = adProduct.value.codes?.split( ',' ) ?? [];
    hasAccess = permissionsStore.hasAccessBasedOnAnyShortCode( adShortCodes );
  }

  return hasAccess;
} );

const showProductAdvertisement = computed< boolean >( () => {
  const hasEditorialAd = !!( editorialAd.value );
  const hasAdProduct = !!( adProduct.value );
  const doesNotAlreadyOwnTheProductBeingAdvertised = !( hasAccessToAdvertisedProduct.value );
  return !isAuthenticated.value ||
    ( hasEditorialAd && hasAdProduct && doesNotAlreadyOwnTheProductBeingAdvertised );
} );

const {
  dataTipForFavoritedStatus,
  favoriteIconClassNameForContent,
  handleCopyContentLink,
  handleFavoritedContentAction,
  handleOpenContentInNewTab,
  showCopyAction
} = useContentActions();
</script>

<template>
  <div class="overflow-x-hidden">
    <div class="flex justify-between">
      <div class="font-bold uppercase tracking-wide text-sm text-jade mb-4">{{ primaryProductName }}</div>
      <div class="flex-shrink-0 min-w-20 space-x-3">
        <span @click.prevent="handleOpenContentInNewTab( content, contentType )" class="tooltip" data-tip="Open in new window">
          <i class="pi pi-external-link"></i>
        </span>
        <span v-if="showCopyAction" @click.prevent="handleCopyContentLink( content, contentType )" class="tooltip" data-tip="Copy link">
          <i class="pi pi-copy"></i>
        </span>
        <span v-if="isAuthenticatedArticleOrWebinar" @click.prevent="handleFavoritedContentAction( content.contentfulId, contentType )" class="tooltip" :data-tip="dataTipForFavoritedStatus( content.contentfulId, contentType )">
          <i class="pi" :class="favoriteIconClassNameForContent( content.contentfulId, contentType )"></i>
        </span>
      </div>
    </div>

    <h1 class="text-2xl" id="title">{{ content.title }}</h1>

    <div class="flex items-center gap-2 mt-5">
      <div>
        <router-link :to="authorRoute">
          <img class="w-12 h-12 rounded-full object-cover" :src="author?.pictureUri" :alt="author?.pictureUri">
        </router-link>
      </div>
      <div>
        <router-link :to="authorRoute" class="font-medium text-base-content">
          {{ author?.name }}
        </router-link>
        <div class="text-sm text-gray-500">{{ contentDateTimeMetaInfo }}</div>
      </div>
    </div>

    <hr class="my-4">

    <section class="body-content flex-1 mx-auto prose prose-xl max-w-screen-xl lg:prose-img:max-w-screen-lg">
      <div v-html="body1" />
      <VimeoEmbed v-if="linkToVideo" :vimeoID="linkToVideo" class="article-video my-8" />
      <div v-if="showProductAdvertisement">
        <div v-html="editorialAd" class="prose-base border-t border-b p-3"/>
      </div>
      <div v-if="majorityTextContent" v-html="body2" />
    </section>

    <div v-if="content.tags.length">
      <hr class="mt-20 mb-10">
      <div class="font-bold text-xs">
        <span>Tags:</span>
        <router-link v-for="( tag ) in content.tags" :key="tag.slug" class="btn btn-xs !text-neutral ms-2" :to="tagRoute( tag.slug )">
          {{ tag.name }}
        </router-link>
      </div>
    </div>

    <div class="mb-10">
      <div>
        <router-link :to="authorRoute">
          <img class="float-left m-3 w-32 h-32 rounded-full object-cover" :src="author?.pictureUri" :alt="author?.pictureUri">
        </router-link>
      </div>
      <div>
        <router-link :to="authorRoute">
          <h4 class="pt-4">{{ author?.name }}</h4>
        </router-link>
        <router-link v-if="isPaidProduct" :to="productRoute">
          {{ primaryProductName }}
        </router-link>
        <h6 v-else>{{ primaryProductName }}</h6>
        <div v-html="author?.shortBiography" />
        <router-link :to="authorRoute">Read more -></router-link>
      </div>

    </div>
    <FreeNewsletter v-if="!isAuthenticated" class="container-sm my-10" />
  </div>
</template>
